import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

export default function MirrorsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Mirrors - Entryway Decor',
    url: `${siteUrl}/entryway/mirrors`,
    description: 'Explore mirrors that brighten and expand your entryway. From full-length designs to decorative pieces, find mirrors that offer both function and style.',
    publisher: {
      '@type': 'Organization',
      name: 'Interior Decor Solutions',
    },
  };

  const products = [
    {
      id: 1,
      name: 'Round Decorative Mirror',
      rating: 4.8,
      reviews: 387,
      image: 'https://public.readdy.ai/ai/img_res/3908594c3a1153651a6044152b4808e3.jpg',
      features: [
        'Natural wood frame with texture',
        'Perfect for above console tables',
        'Diameter: 32"',
        'Easy wall mounting hardware included'
      ]
    },
    {
      id: 2,
      name: 'TRAHOME Irregular Asymmetrical Wall Mirror',
      rating: 4.7,
      reviews: 412,
      image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/90f24cc3f9bdcd636518c76b7000e0b2.jpeg',
      features: [
        'Modern asymmetrical design with unique artistic shape',
        'HD floating glass with explosion-proof backing film',
        'Two-way hanging with 4 hooks for versatile installation',
        'Mirror size: 31.5" x 17.7" perfect for any room'
      ],
      description: 'This distinctive asymmetrical wall mirror features a modern irregular shape that brings artistic atmosphere to your hallway, living room, or bedroom. Made with HD floating glass and explosion-proof film backing, this durable mirror offers secure two-way hanging options for versatile wall decor.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Mirrors - Entryway Decor | Interior Decor Solutions"
        description="Explore mirrors that brighten and expand your entryway. From full-length designs to decorative pieces, find mirrors that offer both function and style."
        keywords="entryway mirrors, full length mirror, decorative mirrors, wall mirrors, round mirror, arched mirror"
        canonical={`${siteUrl}/entryway/mirrors`}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#1A4D4D] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#1A4D4D] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-[70px]">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=beautiful%20modern%20entryway%20with%20elegant%20mirror%20console%20table%20natural%20light%20contemporary%20interior%20design%20sophisticated%20atmosphere%20welcoming%20reflection&width=1920&height=1080&seq=mirror-hero&orientation=landscape"
            alt="Entryway Mirrors"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-20 text-center">
          <div className="mb-4">
            <Link to="/entryway" className="text-white/80 hover:text-white text-sm">Entryway</Link>
            <span className="text-white/60 mx-2">/</span>
            <span className="text-white text-sm">Mirrors</span>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-light mb-4 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Mirrors
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
            Brighten and expand your space
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20">
        <div className="max-w-7xl mx-auto space-y-12 md:space-y-16 lg:space-y-20">
          {products.map((product, index) => (
            <div key={product.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 md:gap-12 items-center ${index % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'} rounded-3xl overflow-hidden p-6 md:p-8 lg:p-12`}>
              <div className="w-full lg:w-1/2">
                <div className="w-full h-[400px] md:h-[450px] lg:h-[500px] rounded-2xl overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain object-center bg-white"
                  />
                </div>
              </div>
              
              <div className="w-full lg:w-1/2">
                <h3 className="text-[#1A4D4D] text-3xl md:text-4xl font-bold mb-4">
                  {product.name}
                </h3>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <i 
                          key={i} 
                          className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-500 text-lg`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm text-gray-600 mt-1">
                      {product.rating} ({product.reviews} reviews)
                    </span>
                  </div>
                </div>

                {product.description && (
                  <p className="text-gray-600 text-base mb-6 leading-relaxed">
                    {product.description}
                  </p>
                )}

                <div className="mb-8">
                  <h4 className="text-[#1A4D4D] font-semibold text-lg mb-4">Key Features:</h4>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-600 text-base">
                        <i className="ri-check-line text-[#1A4D4D] text-xl mt-0.5 flex-shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link 
                  to={product.id === 2 ? '/reviews/trahome-irregular-asymmetrical-wall-mirror' : '/reviews'}
                  className="inline-flex items-center gap-2 bg-[#1A4D4D] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#0F3333] transition-all whitespace-nowrap"
                >
                  View Full Review <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-[#F9F7F4]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-[#1A4D4D] text-3xl md:text-4xl lg:text-5xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Need more inspiration?
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-8">
            Explore our full collection of entryway furniture and decor
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/entryway"
              className="bg-[#1A4D4D] text-white px-8 py-3 rounded-full font-medium hover:bg-[#0F3333] transition-all whitespace-nowrap"
            >
              Browse All Entryway
            </Link>
            <Link 
              to="/blog"
              className="bg-transparent border-2 border-[#1A4D4D] text-[#1A4D4D] px-8 py-3 rounded-full font-medium hover:bg-[#1A4D4D] hover:text-white transition-all whitespace-nowrap"
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-8 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/living-room" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/bedroom" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/kitchen" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-6 md:py-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
